import type { FileTransport } from '@speles7172/file-console';
import { putObject } from '@speles7172/file-console';
import { apiClient } from './client';
import { pdfFirstPageThumbnail } from '../utils/pdfToImages';

/**
 * The seam between `@speles7172/file-console` and this application's API
 * (COMMUNICATIONS-DESIGN C4).
 *
 * A browser cannot query Postgres and must never hold a bucket credential, so
 * the console queries nothing itself: every method here is one endpoint backed
 * by `@speles7172/file-client`, and the access control lives there.
 *
 * ⚠️ **Hold the transport still.** Every component keys its effects on the
 * object's identity, so an object literal written inline in JSX is a new
 * transport on every render and therefore an endless reload. This is a module
 * constant for exactly that reason — documented on every console in this
 * repository, and the trap three of the package's own tests hung a worker on.
 *
 * ⚠️ **Every method is a capability.** A transport with no `deleteFile` renders
 * no delete button. So this exports the full set and lets the *server* decide
 * what may happen — the buttons the console draws come from permissions the
 * server computed, not from what the browser was handed.
 */
export const fileTransport: FileTransport = {
  async tree(nodeId, page) {
    const { data } = await apiClient.get('/files/tree', {
      params: {
        nodeId,
        ...(page?.term ? { term: page.term } : {}),
        ...(page?.cursor ? { cursor: page.cursor } : {}),
      },
    });
    return data;
  },

  async search(term) {
    const { data } = await apiClient.get('/files/search', { params: { term } });
    return data;
  },

  async recent(page) {
    const { data } = await apiClient.get('/files/recent', {
      params: page?.cursor ? { cursor: page.cursor } : {},
    });
    return data;
  },

  // A PDF's tile shows its first page. pdf.js is loaded only when the first
  // PDF tile scrolls into view, so a folder of photographs never fetches it.
  async documentThumbnail({ file, url }) {
    return file.contentType === 'application/pdf' ? pdfFirstPageThumbnail(url) : null;
  },

  async createFolder(input) {
    const { data } = await apiClient.post('/files/folders', input);
    return data;
  },

  async renameFolder({ folderId, name }) {
    const { data } = await apiClient.patch(`/files/folders/${folderId}`, { name });
    return data;
  },

  async moveFolder({ folderId, parentNodeId }) {
    const { data } = await apiClient.patch(`/files/folders/${folderId}/move`, { parentNodeId });
    return data;
  },

  async deleteFolder({ folderId }) {
    await apiClient.delete(`/files/folders/${folderId}`);
  },

  async createUploadUrl(input) {
    const { data } = await apiClient.post('/files/upload-url', input);
    return data;
  },

  async completeUpload({ fileId, byteSize }) {
    const { data } = await apiClient.post(`/files/${fileId}/complete`, { byteSize });
    return data;
  },

  /**
   * The bytes go straight to the presigned URL, not through the API.
   *
   * The package's own implementation, used rather than reimplemented: it
   * reports progress and does not send the application's `Authorization`
   * header to the object store, which S3 rejects — the signature covers the
   * headers, so an extra one invalidates it.
   */
  putObject,

  async createDownloadUrl({ fileId, download }) {
    const { data } = await apiClient.get(`/files/${fileId}/download-url`, {
      params: { download: download ? 'true' : undefined },
    });
    return data;
  },

  async updateFile(input) {
    const { fileId, ...rest } = input;
    const { data } = await apiClient.patch(`/files/${fileId}`, rest);
    return data;
  },

  async copyFile({ fileId, nodeId }) {
    const { data } = await apiClient.post(`/files/${fileId}/copy`, { nodeId });
    return data;
  },

  async deleteFile({ fileId }) {
    await apiClient.delete(`/files/${fileId}`);
  },

  async listEntityFiles({ entityType, entityId, includeFoldered }) {
    const { data } = await apiClient.get('/files/entity', {
      params: { entityType, entityId, includeFoldered: includeFoldered ? 'true' : undefined },
    });
    return data;
  },

  async setFileLinks({ fileId, links }) {
    const { data } = await apiClient.put(`/files/${fileId}/links`, { links });
    return data;
  },

  async describeEntities(refs) {
    const { data } = await apiClient.post('/files/describe-entities', { refs });
    return data;
  },

  async searchEntities({ entityType, term, cursor }) {
    const { data } = await apiClient.get('/files/entities', {
      params: { entityType, term, cursor },
    });
    return data;
  },

  async listFileTypes(entityType) {
    const { data } = await apiClient.get('/files/types', { params: { entityType } });
    return data;
  },

  async upsertFileType(input) {
    const { data } = await apiClient.post('/files/types', input);
    return data;
  },

  async deleteFileType({ id }) {
    // Answers whether a row was actually removed — the console distinguishes
    // that from a tag somebody else had already deleted.
    const { data } = await apiClient.delete<{ deleted: boolean }>(`/files/types/${id}`);
    return data.deleted;
  },
};
